import { useCallback, useState } from 'react';
import { useYearbook } from '../context/YearbookContext';
import { uploadChat, getSession, startBuild, getBuildStatus } from '../api/client';

/**
 * Custom hook for managing chat session operations
 */
export function useSession() {
  const { 
    sessionId, 
    setSession, 
    setStats, 
    setBuildStatus,
    getBuildConfig,
  } = useYearbook();
  
  const [isUploading, setIsUploading] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);
  const [error, setError] = useState(null);
  
  /**
   * Upload a chat file and get analysis
   */
  const upload = useCallback(async (file) => {
    setIsUploading(true);
    setError(null);
    
    try {
      // Upload file
      const uploadResult = await uploadChat(file);
      
      // Get session stats
      const sessionData = await getSession(uploadResult.session_id);
      
      // Update context (session_secret is automatically set by uploadChat)
      setSession(uploadResult.session_id, uploadResult.session_secret, file.name);
      setStats(sessionData);
      
      return { success: true, sessionId: uploadResult.session_id, sessionSecret: uploadResult.session_secret };
    } catch (err) {
      const message = err.response?.data?.detail || 'Upload failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsUploading(false);
    }
  }, [setSession, setStats]);
  
  /**
   * Refresh session stats
   */
  const refresh = useCallback(async () => {
    if (!sessionId) return null;
    
    try {
      const sessionData = await getSession(sessionId);
      setStats(sessionData);
      return sessionData;
    } catch (err) {
      console.error('Failed to refresh session:', err);
      return null;
    }
  }, [sessionId, setStats]);
  
  /**
   * Start yearbook build
   */
  const build = useCallback(async () => {
    if (!sessionId) return { success: false, error: 'No session' };
    
    setIsBuilding(true);
    setError(null);
    
    try {
      const config = getBuildConfig();
      const status = await startBuild(sessionId, config);
      setBuildStatus(status);
      return { success: true, status };
    } catch (err) {
      const message = err.response?.data?.detail || 'Build failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setIsBuilding(false);
    }
  }, [sessionId, getBuildConfig, setBuildStatus]);
  
  /**
   * Poll build status
   */
  const pollStatus = useCallback(async () => {
    if (!sessionId) return null;
    
    try {
      const status = await getBuildStatus(sessionId);
      setBuildStatus(status);
      return status;
    } catch (err) {
      console.error('Failed to get build status:', err);
      return null;
    }
  }, [sessionId, setBuildStatus]);
  
  return {
    sessionId,
    isUploading,
    isBuilding,
    error,
    upload,
    refresh,
    build,
    pollStatus,
  };
}

export default useSession;

